import React from 'react';
import { colPath, f1, niceMax } from './scale';
import { useWidth } from './useWidth';

/**
 * Στήλες (Ανά ημέρα, Ανά ώρα): μία σειρά δεδομένων → ένα χρώμα σε δύο τόνους — 100% για ό,τι τονίζεται
 * (σήμερα, αιχμή), ο ανοιχτός τόνος για τα υπόλοιπα. Στήλες ≤ maxBarWidth px, στρογγυλό μόνο το πάνω άκρο,
 * γραμμή αναφοράς (μ.ο.) πίσω από τις στήλες, τιμές μόνο όπου ζητηθούν.
 */
export interface BarDatum {
  key: string;
  value: number;
  /** Ετικέτα κάτω από τη στήλη (κενό = χωρίς). */
  label?: string;
  /** Δεύτερη, πιο αχνή ετικέτα. */
  sub?: string;
  /** Πλήρης τόνος (αλλιώς ο ανοιχτός). */
  strong?: boolean;
  /** Έντονη ετικέτα (π.χ. «Σήμερα»). */
  bold?: boolean;
  /** Τιμή πάνω από τη στήλη. */
  showValue?: boolean;
  /** Κείμενο για το «ποντίκι» (title). */
  tip?: string;
}

export interface BarsProps {
  data: BarDatum[];
  height: number;
  format: (v: number) => string;
  maxBarWidth?: number;
  /** Πλάτος στήλης ως μέρος της «λωρίδας» της. */
  barRatio?: number;
  reference?: { value: number; label: string } | null;
  ariaLabel: string;
  testId?: string;
}

export interface BarsLayout {
  top: number;
  plotH: number;
  band: number;
  barW: number;
  max: number;
  y: (v: number) => number;
}

export function barsLayout(width: number, height: number, data: BarDatum[], opts: { maxBarWidth: number; barRatio: number; reference?: number | null }): BarsLayout {
  const top = 22;
  const bottom = data.some(d => d.sub) ? 40 : 24;
  const plotH = Math.max(10, height - top - bottom);
  const n = Math.max(1, data.length);
  const band = width / n;
  const barW = Math.max(2, Math.min(opts.maxBarWidth, band * opts.barRatio));
  const peak = Math.max(0, ...data.map(d => d.value), opts.reference ?? 0);
  const max = niceMax(peak * 1.05);
  return { top, plotH, band, barW, max, y: (v: number) => top + plotH * (1 - Math.max(0, v) / max) };
}

export const Bars: React.FC<BarsProps> = ({ data, height, format, maxBarWidth = 24, barRatio = 0.42, reference, ariaLabel, testId }) => {
  const [ref, width] = useWidth();
  const L = barsLayout(width, height, data, { maxBarWidth, barRatio, reference: reference?.value });
  const base = L.top + L.plotH;
  const refY = reference ? f1(L.y(reference.value)) + 0.5 : null;
  return (
    <div ref={ref} className="xh-chart" data-testid={testId}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
        {refY !== null && reference && (
          <g>
            <line className="xh-ref" x1={0} x2={width} y1={refY} y2={refY} />
            <text className="xh-refl" x={0} y={f1(refY - 5)}>{reference.label}</text>
          </g>
        )}
        {data.map((d, i) => {
          const x = i * L.band + (L.band - L.barW) / 2;
          const yv = L.y(d.value);
          const cx = f1(x + L.barW / 2);
          return (
            <g key={d.key} data-testid="bar">
              {d.tip && <title>{d.tip}</title>}
              <rect className="xh-hit" x={f1(i * L.band)} y={L.top} width={f1(L.band)} height={L.plotH} />
              <path className={`xh-bar ${d.strong ? 'v1' : 'v3'}`} d={colPath(x, yv, L.barW, base - yv, 4)} data-strong={d.strong ? 'true' : undefined} data-testid="bar-shape" />
              {d.showValue && d.value > 0 && (
                <text className={`xh-vl${d.strong ? ' b' : ''}`} x={cx} y={f1(yv - 7)} textAnchor="middle">{format(d.value)}</text>
              )}
              {d.label && <text className={`xh-axl${d.bold ? ' b' : ''}`} x={cx} y={base + 18} textAnchor="middle">{d.label}</text>}
              {d.sub && <text className="xh-axl s" x={cx} y={base + 33} textAnchor="middle">{d.sub}</text>}
            </g>
          );
        })}
        <line className="xh-ax" x1={0} x2={width} y1={base + 0.5} y2={base + 0.5} />
      </svg>
    </div>
  );
};

export default Bars;
